/**
 * Centralized upload status constants
 * Single source of truth for all upload status values across the application
 * 
 * Note: The server returns status values that match our internal constants,
 * so no mapping is required between API and internal statuses.
 */

// Internal upload statuses (used in Redux and components)
// These match the status values returned by the server
export const UPLOAD_STATUS = {
    PENDING: 'pending',
    COMPUTING_CHECKSUMS: 'computing_checksums',
    IN_PROGRESS: 'in-progress',
    UPLOADING: 'uploading',
    PAUSED: 'paused',
    COMPLETED: 'completed',
    FAILED: 'failed',
    PARTIALLY_FAILED: 'partially-failed',
    CANCELLED: 'cancelled',
};

// Dataset completion statuses (used for complete dataset API)
export const DATASET_STATUS = {
    UPLOAD_COMPLETED: 'UPLOAD_COMPLETED',
    UPLOAD_ABORTED: 'UPLOAD_ABORTED',
};

// Badge variants for UI display
export const STATUS_BADGE_VARIANTS = {
    [UPLOAD_STATUS.COMPUTING_CHECKSUMS]: 'in-progress',
    [UPLOAD_STATUS.IN_PROGRESS]: 'in-progress',
    [UPLOAD_STATUS.UPLOAD_REGISTERED]: 'in-progress',
    [UPLOAD_STATUS.UPLOAD_RESUMED]: 'in-progress',
    [UPLOAD_STATUS.UPLOAD_IN_PROGRESS]: 'in-progress',
    [UPLOAD_STATUS.UPLOADING]: 'in-progress',
    [UPLOAD_STATUS.COMPLETED]: 'success',
    [UPLOAD_STATUS.FAILED]: 'alert-high',
    [UPLOAD_STATUS.PARTIALLY_FAILED]: 'alert-medium',
    [UPLOAD_STATUS.PAUSED]: 'alert-medium',  // Orange/yellow to indicate action needed
    [UPLOAD_STATUS.CANCELLED]: 'accent-gray',
    [UPLOAD_STATUS.PENDING]: 'accent-gray',
    // Backend dataset statuses
    'UPLOAD_COMPLETED': 'success',   
    'MOVED_TO_QUARANTINE': "success",  // Green
    'UPLOAD_CANCELED': 'alert-high',    
    'UPLOAD_ABORTED': 'alert-high',          // Red
    'ACTIVE': 'alert-medium',     
    'PARTIALLY_FAILED': 'alert-medium',     // Yellow/Orange
};

// User-friendly status display names
export const STATUS_DISPLAY_NAMES = {
    [UPLOAD_STATUS.PENDING]: 'PENDING',
    [UPLOAD_STATUS.COMPUTING_CHECKSUMS]: 'COMPUTING CHECKSUMS',
    [UPLOAD_STATUS.IN_PROGRESS]: 'IN PROGRESS',
    [UPLOAD_STATUS.UPLOADING]: 'UPLOADING',
    [UPLOAD_STATUS.PAUSED]: 'PAUSED',
    [UPLOAD_STATUS.COMPLETED]: 'COMPLETED',
    [UPLOAD_STATUS.FAILED]: 'FAILED',
    [UPLOAD_STATUS.PARTIALLY_FAILED]: 'PARTIALLY FAILED',
    [UPLOAD_STATUS.CANCELLED]: 'CANCELLED',

    // Backend dataset statuses
    'UPLOAD_COMPLETED': 'UPLOAD COMPLETED',
    'MOVED_TO_QUARANTINE': 'MOVED TO QUARANTINE',
    'UPLOAD_CANCELED': 'UPLOAD CANCELED',
    'UPLOAD_ABORTED': 'UPLOAD ABORTED',
    'UPLOAD_IN_PROGRESS': 'UPLOAD IN PROGRESS',
    'UPLOAD_REGISTERED': 'UPLOAD REGISTERED',
    'UPLOAD_RESUMED': 'UPLOAD RESUMED',
    'ACTIVE': 'ACTIVE',
    'PARTIALLY_FAILED': 'PARTIALLY FAILED',
    'PARTIALLY_COMPLETED': 'PARTIALLY COMPLETED',

    // Common lowercase variants
    'completed': 'COMPLETED',
    'in-progress': 'IN PROGRESS',
    'failed': 'FAILED',
    'pending': 'PENDING',
    'cancelled': 'CANCELLED',
    'paused': 'PAUSED'
};

// Helper function: Check if status indicates active upload
export const isActiveStatus = (status) => {
    return [
        UPLOAD_STATUS.PENDING,
        UPLOAD_STATUS.COMPUTING_CHECKSUMS,
        UPLOAD_STATUS.IN_PROGRESS,
        UPLOAD_STATUS.UPLOADING,
    ].includes(status);
};

// Helper function: Check if status indicates completion
export const isCompletedStatus = (status) => {
    return status === UPLOAD_STATUS.COMPLETED;
};

// Helper function: Check if status indicates failure
export const isFailedStatus = (status) => {
    return [
        UPLOAD_STATUS.FAILED,
        UPLOAD_STATUS.PARTIALLY_FAILED,
        UPLOAD_STATUS.CANCELLED,
    ].includes(status);
};

// Helper function: Check if status is retryable
export const isRetryableStatus = (status) => {
    return [
        UPLOAD_STATUS.FAILED,
        UPLOAD_STATUS.PARTIALLY_FAILED,
        UPLOAD_STATUS.PAUSED,
        UPLOAD_STATUS.CANCELLED,
    ].includes(status);
};

// Helper function: Get badge variant for status
export const getStatusBadgeVariant = (status) => {
    return STATUS_BADGE_VARIANTS[status] || 'accent-gray';
};

// Helper function: Get display name for status
export const getStatusDisplayName = (status) => {
    return STATUS_DISPLAY_NAMES[status] || status.replace(/_/g, ' ').toUpperCase();
};